import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import styled from 'styled-components';

import { InGameCurrency, Rarity } from '#src/__generated__/globalTypes.ts';
import fiftyFiftyCraftClueUrl from '#src/assets/crafting-clues/best-five.webp';
import bestStarRankCraftClueUrl from '#src/assets/crafting-clues/best-star-rank.webp';
import competitionCraftClueUrl from '#src/assets/crafting-clues/competition.webp';
import divisionClueUrl from '#src/assets/crafting-clues/division.webp';
import countryCraftClueUrl from '#src/assets/crafting-clues/nationality.webp';
import positionClueUrl from '#src/assets/crafting-clues/position.webp';
import shuffleCraftClueUrl from '#src/assets/crafting-clues/shuffle.webp';
import { FRONTEND_ASSET_HOST } from '#src/constants/assets.ts';
import { isType } from '#src/lib/gql/index.ts';
import {
  isXPCurrency,
  stripRarityFromCraftingClue,
} from '#src/lib/rewards.tsx';
import { inGameCurrencyToRarity } from '#src/lib/scarcity.ts';
import type { Unit } from '#src/lib/style.ts';
import { toSnakeCase } from '#src/lib/toSnakeCase.ts';
import {
  isGoldenWheelTicketCurrency,
  isWheelTicketCurrency,
} from '#src/lib/wheelCurrencies.ts';

import type { RewardConfigImage_anyRewardConfigInterface } from './__generated__/index.graphql.ts';

const REWARDS_BASE_URL = `${FRONTEND_ASSET_HOST}/rewards/`;

const Img = styled.img`
  object-fit: contain;
`;

const getRewardImageUrl = (
  anyRewardConfig: RewardConfigImage_anyRewardConfigInterface,
  rarity: Rarity | 'mix' | undefined
): string => {
  if (isType(anyRewardConfig, 'ShopItemRewardConfig')) {
    return anyRewardConfig.shopItem.pictureUrl;
  }
  if (
    isType(anyRewardConfig, 'ExperienceRewardConfig') &&
    anyRewardConfig.pictureUrl
  ) {
    return anyRewardConfig.pictureUrl;
  }
  if (
    isType(anyRewardConfig, 'CardPacksRewardConfig') &&
    anyRewardConfig.cardPack?.pictureUrl
  ) {
    return anyRewardConfig.cardPack.pictureUrl;
  }

  let url: string = '';
  switch (anyRewardConfig.__typename) {
    case 'CardPacksRewardConfig':
      url = `card-common`;
      break;
    case 'CardRewardConfig':
      url = `card-${toSnakeCase(rarity ?? anyRewardConfig.configRarity.toLowerCase())}`;
      break;
    case 'CardShardRewardConfig':
      if (anyRewardConfig.cardSet?.slug) {
        url = `shard-${toSnakeCase(anyRewardConfig.cardSet.slug.toLowerCase())}`;
      } else {
        url = `shard-${toSnakeCase(rarity ?? anyRewardConfig.configRarity.toLowerCase())}`;
      }
      break;
    case 'ConversionCreditRewardConfig':
      url = anyRewardConfig.percentageDiscount === 1 ? `credit-100` : `credit`;
      break;
    case 'MonetaryRewardConfig':
      url = `cash`;
      break;
    case 'ExperienceRewardConfig':
      url = `experience`;
      break;
    case 'DeliverableItemRewardConfig':
      if (isType(anyRewardConfig.deliverableItem, 'JerseyDeliverableItem')) {
        url = `jersey`;
      }
      if (isType(anyRewardConfig.deliverableItem, 'WearableDeliverableItem')) {
        url = `hat`;
      }
      if (
        isType(anyRewardConfig.deliverableItem, 'ExperienceDeliverableItem')
      ) {
        url = `experience`;
      }
      if (isType(anyRewardConfig.deliverableItem, 'TicketsDeliverableItem')) {
        url = `ticket`;
      }
      break;
    case 'ProbabilisticBundleRewardConfig':
      url = `box-${toSnakeCase(anyRewardConfig.probabilisticBundleConfig.rarity.toLowerCase())}`;
      break;
    case 'InGameCurrencyRewardConfig': {
      const currency = stripRarityFromCraftingClue(anyRewardConfig.currency);
      if (isXPCurrency(currency)) {
        url = `xp-${toSnakeCase(((rarity === 'mix' && rarity) || inGameCurrencyToRarity[currency] || Rarity.common).toLowerCase())}`;
      }
      if (currency === InGameCurrency.FIFTY_FIFTY_CRAFT_CLUE) {
        return fiftyFiftyCraftClueUrl;
      }
      if (currency === InGameCurrency.COMPETITION_CRAFT_CLUE) {
        return competitionCraftClueUrl;
      }
      if (currency === InGameCurrency.COUNTRY_CRAFT_CLUE) {
        return countryCraftClueUrl;
      }
      if (currency === InGameCurrency.POSITION_CRAFT_CLUE) {
        return positionClueUrl;
      }
      if (currency === InGameCurrency.DIVISION_CRAFT_CLUE) {
        return divisionClueUrl;
      }
      if (currency === InGameCurrency.BEST_STAR_RANK_CRAFT_CLUE) {
        return bestStarRankCraftClueUrl;
      }
      if (currency === InGameCurrency.SPECIAL_EVENT_CRAFT_CLUE) {
        return shuffleCraftClueUrl;
      }
      if (currency === InGameCurrency.COMMON_GEM) {
        url = `common-gem`;
      }
      if (isWheelTicketCurrency(currency)) {
        url = `wheel-ticket-${toSnakeCase(inGameCurrencyToRarity[currency] ?? Rarity.common).toLowerCase()}`;
      }
      if (isGoldenWheelTicketCurrency(currency)) {
        url = 'wheel-ticket-limited';
      }
      if (currency === InGameCurrency.SET_RAFFLE_TICKET) {
        url = 'set-raffle-ticket';
      }
      break;
    }
  }

  return `${REWARDS_BASE_URL}${url}.png`;
};

type Props = {
  anyRewardConfig: RewardConfigImage_anyRewardConfigInterface;
  rarity?: Rarity | 'mix';
  size?: Unit;
  alt?: string;
};

export const RewardConfigImage = ({
  anyRewardConfig,
  rarity,
  size = 3,
  alt = '',
}: Props) => {
  const url = getRewardImageUrl(anyRewardConfig, rarity);

  return <Img src={url} alt={alt} width={size * 8} height={size * 8} />;
};

RewardConfigImage.fragments = {
  anyRewardConfigInterface: gql`
    fragment RewardConfigImage_anyRewardConfigInterface on AnyRewardConfigInterface {
      id
      ... on ConversionCreditRewardConfig {
        id
        percentageDiscount
      }
      ... on CardRewardConfig {
        id
        configRarity
      }
      ... on CardShardRewardConfig {
        id
        configRarity
        cardSet {
          slug
        }
      }
      ... on ProbabilisticBundleRewardConfig {
        id
        probabilisticBundleConfig {
          id
          rarity
        }
      }
      ... on InGameCurrencyRewardConfig {
        id
        currency
      }
      ... on ShopItemRewardConfig {
        id
        shopItem {
          id
          pictureUrl
        }
      }
      ... on DeliverableItemRewardConfig {
        id
        deliverableItem {
          slug
        }
      }
      ... on ExperienceRewardConfig {
        id
        pictureUrl
      }
      ... on CardPacksRewardConfig {
        id
        cardPack {
          slug
          pictureUrl
        }
      }
    }
  ` as TypedDocumentNode<RewardConfigImage_anyRewardConfigInterface>,
};
